'use client';

import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { createContext, Fragment, useContext, useEffect, useRef, type ReactNode } from 'react';
import { bootRealmSeedConflictsWithStoreActor } from '../src/lib/auth/server-seed-admission';
import {
  captureReactQueryError,
  projectReactQueryKeyForTelemetry,
} from '../src/lib/sentry-client-observability';
import { shouldCaptureQueryError } from '../src/lib/query-errors';
import { useAuthStore, type AuthUserProfile } from '../src/stores/auth-store';
import {
  abandonQueryClientRealm,
  buildQueryAuthIdentity,
  queryAuthIdentityChanged,
  shouldRotateQueryAuthRealm,
  type QueryAuthIdentity,
} from './query-auth-identity';

function createDashboardQueryClient() {
  return new QueryClient({
    queryCache: new QueryCache({
      onError: (error, query) => {
        if (!shouldCaptureQueryError(error)) return;
        captureReactQueryError(error, {
          source: 'react_query_error',
          operation: 'query',
          queryKey: projectReactQueryKeyForTelemetry(query.queryKey),
          status: query.state.status,
          failureCount: query.state.fetchFailureCount,
        });
      },
    }),
    mutationCache: new MutationCache({
      onError: (error, _variables, _context, mutation) => {
        captureReactQueryError(error, {
          source: 'react_mutation_error',
          operation: 'mutation',
          mutationKey: projectReactQueryKeyForTelemetry(mutation.options.mutationKey),
        });
      },
    }),
    defaultOptions: {
      queries: {
        staleTime: 30 * 1000,
        retry: 1,
        refetchOnWindowFocus: false,
      },
    },
  });
}

type QueryAuthRealm = {
  generation: number;
  client: QueryClient;
};

function createInitialRealm(): QueryAuthRealm {
  return {
    generation: 0,
    client: createDashboardQueryClient(),
  };
}

export type QueryAuthBoundaryValue = {
  captureVerifiedWriteGeneration: () => number;
  hasVerifiedWrite: () => boolean;
  notifyVerifiedIdentityWrite: (
    previous: AuthUserProfile | null,
    next: AuthUserProfile | null,
  ) => void;
  getLastCommittedIdentity: () => QueryAuthIdentity | null;
  getLastVerifiedIdentity: () => QueryAuthIdentity | null;
  publishAdmittedServerSeed: (user: AuthUserProfile | null) => void;
  readAdmittedServerSeed: () => AuthUserProfile | null;
};

export const QueryAuthBoundaryContext = createContext<QueryAuthBoundaryValue | null>(null);

export function useQueryAuthBoundary(): QueryAuthBoundaryValue {
  const value = useContext(QueryAuthBoundaryContext);
  if (!value) {
    throw new Error('useQueryAuthBoundary requires QueryProvider');
  }
  return value;
}

export function QueryProvider({ children }: { children: ReactNode }) {
  const committedNonNullIdentityRef = useRef<QueryAuthIdentity | null>(null);
  const admittedServerSeedRef = useRef<AuthUserProfile | null>(null);
  const verifiedWriteGenerationRef = useRef(0);
  const hasVerifiedWriteRef = useRef(false);
  const lastVerifiedIdentityRef = useRef<QueryAuthIdentity | null>(null);
  const obsoleteClientRef = useRef<QueryClient | null>(null);
  const realmRef = useRef<QueryAuthRealm | null>(null);
  if (!realmRef.current) {
    realmRef.current = createInitialRealm();
  }

  const boundaryApiRef = useRef<QueryAuthBoundaryValue | null>(null);
  if (!boundaryApiRef.current) {
    boundaryApiRef.current = {
      captureVerifiedWriteGeneration: () => verifiedWriteGenerationRef.current,
      hasVerifiedWrite: () => hasVerifiedWriteRef.current,
      notifyVerifiedIdentityWrite: (previous, next) => {
        hasVerifiedWriteRef.current = true;
        lastVerifiedIdentityRef.current = buildQueryAuthIdentity(next);
        if (queryAuthIdentityChanged(previous, next)) {
          verifiedWriteGenerationRef.current += 1;
        }
      },
      getLastCommittedIdentity: () => committedNonNullIdentityRef.current,
      getLastVerifiedIdentity: () => lastVerifiedIdentityRef.current,
      publishAdmittedServerSeed: (user) => {
        admittedServerSeedRef.current = user;
      },
      readAdmittedServerSeed: () => admittedServerSeedRef.current,
    };
  }

  const user = useAuthStore((state) => state.user);

  /**
   * Rotate to a fresh QueryClient render realm when the committed non-null actor
   * identity changes, or when the boot realm was SSR-seeded for a different
   * actor than the store's first non-null identity. A new client plus a keyed
   * child subtree isolates the next actor; old-client cleanup runs after commit
   * so render does not notify obsolete observers.
   *
   * Transient auth nulls keep the committed identity and realm. A→null→A keeps
   * the warm cache. null→first actor keeps legitimate SSR/client seed when the
   * admitted navigation seed matches the store actor.
   */
  const nextIdentity = buildQueryAuthIdentity(user);
  const committedNonNullIdentity = committedNonNullIdentityRef.current;
  let rotateRealm = shouldRotateQueryAuthRealm(committedNonNullIdentity, nextIdentity);
  if (
    !rotateRealm &&
    committedNonNullIdentity === null &&
    nextIdentity !== null &&
    bootRealmSeedConflictsWithStoreActor(nextIdentity, admittedServerSeedRef.current)
  ) {
    rotateRealm = true;
  }

  if (rotateRealm) {
    obsoleteClientRef.current = realmRef.current!.client;
    admittedServerSeedRef.current = null;
    realmRef.current = {
      generation: realmRef.current!.generation + 1,
      client: createDashboardQueryClient(),
    };
  }
  if (nextIdentity !== null) {
    committedNonNullIdentityRef.current = nextIdentity;
  }

  const realm = realmRef.current!;

  useEffect(() => {
    const obsolete = obsoleteClientRef.current;
    obsoleteClientRef.current = null;
    if (obsolete) {
      abandonQueryClientRealm(obsolete);
    }
  });

  return (
    <QueryAuthBoundaryContext.Provider value={boundaryApiRef.current}>
      <QueryClientProvider client={realm.client}>
        <Fragment key={realm.generation}>{children}</Fragment>
      </QueryClientProvider>
    </QueryAuthBoundaryContext.Provider>
  );
}
